import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Trash2, RefreshCw, ExternalLink } from 'lucide-react';

interface YouTubeChannel {
  id: string;
  channel_id: string;
  channel_name: string;
  custom_url?: string | null;
  thumbnail_url?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const YouTubeChannelsContent: React.FC = () => {
  const { theme } = useTheme();
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingChannel, setEditingChannel] = useState<YouTubeChannel | null>(null);
  const [formData, setFormData] = useState({
    channel_id: '',
    channel_name: '',
    custom_url: '',
    thumbnail_url: '',
    is_active: true,
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isFetchingVideos, setIsFetchingVideos] = useState<boolean>(false);

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('youtube_channels')
        .select('*')
        .order('channel_name', { ascending: true });

      if (fetchError) throw fetchError;
      setChannels(data || []);
    } catch (e: any) {
      console.error('Error fetching YouTube channels:', e);
      setError('Falha ao carregar canais. Tente novamente.');
      toast.error('Falha ao carregar canais.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: checkbox.checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const openModalForCreate = () => {
    setEditingChannel(null);
    setFormData({
      channel_id: '',
      channel_name: '',
      custom_url: '',
      thumbnail_url: '',
      is_active: true,
    });
    setShowModal(true);
  };

  const openModalForEdit = (channel: YouTubeChannel) => {
    setEditingChannel(channel);
    setFormData({
      channel_id: channel.channel_id,
      channel_name: channel.channel_name,
      custom_url: channel.custom_url || '',
      thumbnail_url: channel.thumbnail_url || '',
      is_active: channel.is_active,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.channel_id || !formData.channel_name) {
      toast.error('ID do Canal e Nome do Canal são obrigatórios.');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingChannel) {
        // Update
        const { error: updateError } = await supabase
          .from('youtube_channels')
          .update({
            channel_id: formData.channel_id,
            channel_name: formData.channel_name,
            custom_url: formData.custom_url || null,
            thumbnail_url: formData.thumbnail_url || null,
            is_active: formData.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingChannel.id);
        if (updateError) throw updateError;
        toast.success('Canal atualizado com sucesso!');
      } else {
        // Create
        const { error: insertError } = await supabase
          .from('youtube_channels')
          .insert([{
            channel_id: formData.channel_id,
            channel_name: formData.channel_name,
            custom_url: formData.custom_url || null,
            thumbnail_url: formData.thumbnail_url || null,
            is_active: formData.is_active,
          }]);
        if (insertError) throw insertError;
        toast.success('Canal adicionado com sucesso!');
      }
      setShowModal(false);
      fetchChannels(); // Refresh
    } catch (e: any) {
      console.error('Error saving channel:', e);
      const errorMessage = e.message?.includes('youtube_channels_channel_id_key') 
        ? 'Já existe um canal com este ID do YouTube.'
        : 'Falha ao salvar canal.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (channelId: string, channelName: string) => {
    toast((t) => (
      <div className={`flex flex-col items-center p-2 rounded-md ${theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}>
        <p className="mb-3 text-center">
          Tem certeza que deseja remover o canal <strong className="font-semibold">{channelName}</strong>?
          <br />
          <span className="text-xs text-red-500 dark:text-red-400">Isso também removerá todos os vídeos associados a ele.</span>
        </p>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              setIsSubmitting(true); // Re-use for delete operation indication
              try {
                const { error: deleteError } = await supabase
                  .from('youtube_channels')
                  .delete()
                  .eq('id', channelId);
                if (deleteError) throw deleteError;
                toast.success(`Canal "${channelName}" removido com sucesso.`);
                fetchChannels(); // Refresh
              } catch (e: any) {
                console.error('Error deleting channel:', e);
                toast.error('Falha ao remover canal.');
              } finally {
                setIsSubmitting(false);
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
          >
            Remover
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className={`px-4 py-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium ${
              theme === 'dark' ? 'bg-gray-500 text-gray-200' : 'bg-gray-200 text-gray-800'
            }`}
          >
            Cancelar
          </button>
        </div>
      </div>
    ), { duration: Infinity, id: `delete-channel-${channelId}` });
  };
  
  const handleFetchVideosManually = async () => {
    setIsFetchingVideos(true);
    toast.loading('Iniciando busca de vídeos...', { id: 'manual-fetch-toast' });
    try {
      const { data, error } = await supabase.functions.invoke('fetch-youtube-videos', {
        // Se quiser buscar para um canal específico, adicione o ID do canal aqui
        // body: { channelId: 'SPECIFIC_YOUTUBE_CHANNEL_ID' } 
      });

      if (error) throw error;

      if (data && data.error) {
         throw new Error(data.error.message || data.error);
      }
      
      const summary = data; // A resposta da Edge Function já é o sumário
      toast.success(
        `Busca concluída! Canais processados: ${summary.channelsProcessed}, Novos vídeos: ${summary.newVideosInserted}.`,
        { id: 'manual-fetch-toast', duration: 5000 }
      );
    } catch (e: any) {
      console.error('Error manually fetching videos:', e);
      toast.error(`Falha ao buscar vídeos: ${e.message || 'Erro desconhecido'}`, { id: 'manual-fetch-toast' });
    } finally {
      setIsFetchingVideos(false);
    }
  };


  if (loading) {
    return <div className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Carregando canais...</div>;
  }

  if (error) {
    return <div className="text-red-500 dark:text-red-400 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">{error}</div>;
  }

  return (
    <div className={`p-4 md:p-6 rounded-lg shadow-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>
            Canais do YouTube
          </h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Gerencie os canais do YouTube cujos vídeos serão exibidos na HomePage.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={openModalForCreate}
            disabled={isSubmitting || isFetchingVideos}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm flex items-center gap-2 transition-colors
                        ${isSubmitting || isFetchingVideos ? 'bg-gray-400 cursor-not-allowed' : 
                          (theme === 'dark' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600')}`}
          >
            <Plus size={18} /> Adicionar Canal
          </button>
          <button
            onClick={handleFetchVideosManually}
            disabled={isSubmitting || isFetchingVideos}
            className={`px-4 py-2 text-sm font-medium rounded-md shadow-sm flex items-center gap-2 transition-colors
                        ${isSubmitting || isFetchingVideos ? 'bg-gray-400 cursor-not-allowed' :
                          (theme === 'dark' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white')}`}
          >
            <RefreshCw size={18} className={isFetchingVideos ? 'animate-spin' : ''} /> 
            {isFetchingVideos ? 'Buscando Vídeos...' : 'Buscar Vídeos Agora'}
          </button>
        </div>
      </div>

      {channels.length === 0 && !loading && (
        <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-center py-8`}>
          Nenhum canal do YouTube cadastrado. Clique em "Adicionar Canal" para começar.
        </p>
      )}

      {channels.length > 0 && (
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <tr>
                <th scope="col" className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Canal</th>
                <th scope="col" className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>ID YouTube</th>
                <th scope="col" className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Status</th>
                <th scope="col" className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Ações</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${theme === 'dark' ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {channels.map(channel => (
                <tr key={channel.id} className={`${theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50/50'}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      {channel.thumbnail_url && (
                        <div className="flex-shrink-0 h-10 w-10">
                          <img className="h-10 w-10 rounded-full object-cover" src={channel.thumbnail_url} alt={channel.channel_name} />
                        </div>
                      )}
                      <div className={`ml-3 ${!channel.thumbnail_url && 'ml-0'}`}>
                        <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{channel.channel_name}</div>
                        {channel.custom_url && (
                           <a 
                             href={`https://www.youtube.com/${channel.custom_url.startsWith('@') ? '' : 'c/'}${channel.custom_url}`} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className={`text-xs flex items-center gap-1 ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                           >
                             {channel.custom_url} <ExternalLink size={12}/>
                           </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>{channel.channel_id}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      channel.is_active 
                        ? (theme === 'dark' ? 'bg-green-700 text-green-100' : 'bg-green-100 text-green-800')
                        : (theme === 'dark' ? 'bg-red-700 text-red-100' : 'bg-red-100 text-red-800')
                    }`}>
                      {channel.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openModalForEdit(channel)}
                        disabled={isSubmitting || isFetchingVideos}
                        className={`p-1.5 rounded ${theme === 'dark' ? 'text-yellow-400 hover:text-yellow-300 hover:bg-gray-700' : 'text-yellow-600 hover:text-yellow-800 hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed`}
                        title="Editar Canal"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(channel.id, channel.channel_name)}
                        disabled={isSubmitting || isFetchingVideos}
                        className={`p-1.5 rounded ${theme === 'dark' ? 'text-red-400 hover:text-red-300 hover:bg-gray-700' : 'text-red-600 hover:text-red-800 hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed`}
                        title="Remover Canal"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal para Adicionar/Editar Canal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
          <div className={`rounded-lg shadow-xl w-full max-w-lg ${theme === 'dark' ? 'bg-gray-800 text-gray-100' : 'bg-white'}`}>
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <h3 className={`text-xl font-semibold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {editingChannel ? 'Editar Canal do YouTube' : 'Adicionar Novo Canal'}
                </h3>
                <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Preencha os detalhes do canal. O "ID do Canal" é encontrado na URL do canal (ex: UC_x5XG1OV2P6uZZ5FSM9Ttw).
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="channel_name" className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Nome do Canal *</label>
                    <input
                      type="text"
                      name="channel_name"
                      id="channel_name"
                      value={formData.channel_name}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500' 
                          : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                      placeholder="Ex: Boardverso Oficial"
                    />
                  </div>
                  <div>
                    <label htmlFor="channel_id" className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>ID do Canal no YouTube *</label>
                    <input
                      type="text"
                      name="channel_id"
                      id="channel_id"
                      value={formData.channel_id}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500' 
                          : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                      placeholder="Ex: UC_x5XG1OV2P6uZZ5FSM9Ttw"
                    />
                  </div>
                  <div>
                    <label htmlFor="custom_url" className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>URL Customizada (Opcional)</label>
                    <input
                      type="text"
                      name="custom_url"
                      id="custom_url"
                      value={formData.custom_url}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500' 
                          : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                      placeholder="Ex: @boardverso ou boardversochannel"
                    />
                  </div>
                  <div>
                    <label htmlFor="thumbnail_url" className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>URL da Thumbnail (Opcional)</label>
                    <input
                      type="text"
                      name="thumbnail_url"
                      id="thumbnail_url"
                      value={formData.thumbnail_url}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500' 
                          : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                      placeholder="URL da imagem de perfil do canal"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                        theme === 'dark' ? 'bg-gray-600 border-gray-500 checked:bg-indigo-500' : ''
                      }`}
                    />
                    <label htmlFor="is_active" className={`ml-2 block text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Ativo (buscar vídeos deste canal)
                    </label>
                  </div>
                </div>
              </div>
              <div className={`px-6 py-4 border-t flex justify-end gap-3 ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'bg-gray-50'}`}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-medium rounded-md border shadow-sm transition-colors
                              ${theme === 'dark' 
                                ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:bg-gray-700 disabled:text-gray-500' 
                                : 'border-gray-300 text-gray-700 hover:bg-gray-100 disabled:bg-gray-200 disabled:text-gray-400'}`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isFetchingVideos}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm flex items-center justify-center transition-colors
                              ${isSubmitting || isFetchingVideos ? 'bg-gray-400 cursor-not-allowed' :
                                (theme === 'dark' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600')}`}
                >
                  {isSubmitting ? 'Salvando...' : (editingChannel ? 'Salvar Alterações' : 'Adicionar Canal')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeChannelsContent; 